import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { FormPageHeader, Input, ToggleInput } from 'components/Shared';
import { FORM_BACK_ACTION } from 'constants/Common';
import type { UpdateAreaData } from 'contracts/Areas';
import { useValidation } from 'hooks';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from 'store';
import { ListAreasActions, UpdateAreaActions } from 'store/ducks/areas';
import { UpdateAreaValidator } from 'validators/Areas';
import * as S from './styles';

interface Props {
  areaId: string | number;
  onUpdate?: () => void;
}

export const AreaUpdateForm = ({ areaId, onUpdate }: Props) => {
  const dispatch: AppDispatch = useDispatch();
  const formRef = useRef<FormHandles>(null);
  const { handleApiErrors, handleFormErrors } = useValidation();
  const { data: areas, loading: loadingAreas } = useSelector(
    (state: RootState) => state.listAreas
  );
  const { loading: updatingArea, validationErrors } = useSelector(
    (state: RootState) => state.updateArea
  );
  const area = useMemo(
    () => areas.find(({ id }) => String(id) === String(areaId)),
    [areaId, areas]
  );

  const onSubmit = useCallback(
    async (data: UpdateAreaData): Promise<void> => {
      try {
        formRef.current?.setErrors({});
        const validData = await new UpdateAreaValidator().schema.validate(
          {
            ...data,
            isDockManagement: String(data.isDockManagement) === '1',
          },
          { abortEarly: false }
        );
        dispatch(UpdateAreaActions.request(Number(areaId), validData, onUpdate));
      } catch (error) {
        handleFormErrors(error, formRef);
      }
    },
    [areaId, dispatch, handleFormErrors, onUpdate]
  );

  useEffect(() => {
    dispatch(ListAreasActions.request());
  }, [dispatch]);

  useEffect(() => {
    if (area)
      formRef.current?.setData({
        name: area.name,
        code: area.code,
        isDockManagement: area.isDockManagement ? '1' : '0',
      });
  }, [area]);

  useEffect(() => {
    handleApiErrors(validationErrors, formRef);
  }, [handleApiErrors, validationErrors]);

  useEffect(
    () => () => {
      dispatch(ListAreasActions.reset());
      dispatch(UpdateAreaActions.reset());
    },
    [dispatch]
  );

  return (
    <S.MainPanel>
      <FormPageHeader
        title="Editar área"
        icon={<S.AreaIcon />}
        isLoading={loadingAreas}
        actions={
          <S.LinkButton size="small" to="/configuracoes/areas">
            <S.ArrowLeftIcon /> Voltar
          </S.LinkButton>
        }
      />
      <Form ref={formRef} onSubmit={onSubmit}>
        <S.FormRow>
          <Input name="name" label="Nome" />
          <Input name="code" label="Código" />
          <ToggleInput
            name="isDockManagement"
            label="Disponível para gestão de docas/armazém"
          />
        </S.FormRow>
        <S.FormActions>
          <S.LinkButton mood="light" to="/configuracoes/areas">
            {FORM_BACK_ACTION}
          </S.LinkButton>
          <S.Button type="submit">
            {updatingArea ? <S.ActivityIndicator /> : 'Salvar'}
          </S.Button>
        </S.FormActions>
      </Form>
    </S.MainPanel>
  );
};
